import React, { useState } from "react";
import { motion } from "framer-motion";
import DateOfBirthFields from "./forms/DateOfBirthFields";
import AcademicFields from "./forms/AcademicFields";
import WorkFields from "./forms/WorkFields";
import NationalityFields from "./forms/NationalityFields";
import LinkedInField from "./forms/LinkedInField";
import GenderField from "./forms/GenderField";
import DocumentUpload from "./forms/DocumentUpload";
import SubmitButton from "./forms/SubmitButton";
import ApplicationPersonalInfo from "./ApplicationPersonalInfo";
import { useForm, FormProvider } from "react-hook-form";
import { ApplicationFormType, applicationFormSchema } from "@/lib/form-schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  eligibilityCriteriaToTierMapping,
  tierToEligibilityCriteriaMapping,
} from "@/components/forms/academic/MembershipTierSelect";

const ApplicationForm = () => {
  const methods = useForm<ApplicationFormType>({
    resolver: zodResolver(applicationFormSchema),
    defaultValues: {
      linkedin_url: "",
      is_employed: false,
      industry: "",
      position: "",
      employer: "",
      tier: "Group 1",
      gender: "female",
      date_of_birth: null,
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      nationality: "Bahraini",
      cpr_number: "",
      degrees: [],
      uploadedDocuments: [],
    },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isSubmitting, errors, isValid },
  } = methods;

  const [countryCode, setCountryCode] = useState("973"); // Bahrain default

  // Tier select handler
  const handleTierChange = (value: string) => {
    setValue("tier", eligibilityCriteriaToTierMapping[value] || value);
  };

  // Submission handler
  const onSubmit = async (data: ApplicationFormType) => {
    try {
      // This should never be null due to form validation, but adding safety check
      if (!data.date_of_birth) {
        toast.error("Please select your date of birth");
        return;
      }

      const dateOfBirth = data.date_of_birth.toISOString().split("T")[0];

      // Insert application and get the ID
      const { data: applicationData, error: applicationError } = await supabase
        .from("applications")
        .insert({
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone_number: `+${countryCode}${data.phone_number}`,
          date_of_birth: dateOfBirth,
          industry: data.is_employed ? data.industry : null,
          position: data.is_employed ? data.position : null,
          employer: data.is_employed ? data.employer : null,
          tier: data.tier,
          is_employed: data.is_employed,
          linkedin_url: data.linkedin_url || null,
          nationality: data.nationality,
          cpr_number: data.cpr_number,
          gender: data.gender,
          institution: null,
          university_grad_year: null,
          school_grad_year: null,
        })
        .select()
        .single();

      if (applicationError) {
        console.error("Error submitting application:", applicationError);
        toast.error("Failed to submit application. Please try again.");
        return;
      }

      // Insert degrees into application_degrees table
      if (data.degrees && data.degrees.length > 0) {
        const degreesToInsert = data.degrees.map((degree) => ({
          application_id: applicationData.id,
          level: degree.degreeLevel,
          institution_id:
            degree.institution === "other" ? undefined : degree.institution,
          institution_other_name:
            degree.institution === "other"
              ? degree.institution_other_name
              : null,
          degree_title: degree.degree_title,
          major: degree.major,
          graduation_year: degree.graduationYear,
          gpa: degree.gpa,
          is_valedictorian: degree.is_valedictorian || false,
          is_awarded: true,
        }));

        const { error: degreesError } = await supabase
          .from("application_degrees")
          .insert(degreesToInsert);

        if (degreesError) {
          console.error("Error submitting degrees:", degreesError);
          toast.error(
            "Application submitted but there was an issue with degree information."
          );
        }
      }

      // Insert uploaded documents into application_documents table
      if (data.uploadedDocuments && data.uploadedDocuments.length > 0) {
        const documentsToInsert = data.uploadedDocuments.map((doc) => ({
          application_id: applicationData.id,
          file_name: doc.file_name,
          file_type: doc.file_type,
          storage_path: doc.storage_path,
        }));

        const { error: documentsError } = await supabase
          .from("application_documents")
          .insert(documentsToInsert);

        if (documentsError) {
          console.error("Error submitting documents:", documentsError);
          toast.error(
            "Application submitted but there was an issue with document uploads."
          );
        }
      }

      // Send application emails
      try {
        // Prepare application data for email
        const emailApplicationData = {
          id: applicationData.id,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone_number: data.phone_number,
          date_of_birth: dateOfBirth,
          nationality: data.nationality,
          gender: data.gender,
          tier: data.tier,
          is_employed: data.is_employed,
          industry: data.industry,
          position: data.position,
          employer: data.employer,
          linkedin_url: data.linkedin_url,
          cpr_number: data.cpr_number,
          degrees: data.degrees.map((degree) => ({
            level: degree.degreeLevel,
            institution_id:
              degree.institution !== "other" ? degree.institution : undefined,
            institution_other_name:
              degree.institution === "other"
                ? degree.institution_other_name
                : undefined,
            degree_title: degree.degree_title,
            major: degree.major,
            graduation_year: degree.graduationYear,
            gpa: degree.gpa,
            is_valedictorian: degree.is_valedictorian,
          })),
          documents: data.uploadedDocuments.map((doc) => ({
            file_name: doc.file_name,
            file_type: doc.file_type,
          })),
        };

        const { error: emailError } = await supabase.functions.invoke(
          "send-application-emails",
          {
            body: { applicationData: emailApplicationData },
          }
        );

        if (emailError) {
          console.error("Error sending emails:", emailError);
        } else {
          console.log("Application emails sent successfully");
        }
      } catch (emailError) {
        console.error("Error calling email function:", emailError);
      }

      toast.success("Application submitted successfully! Your application has been received and will be reviewed.");

      // Reset the form to clear all fields
      reset();
      
      // Reset country code to default
      setCountryCode("973");

      // Show success message instead of navigating
      if (window.parent !== window) {
        // If inside iframe, post message to parent
        window.parent.postMessage(
          { type: "APPLICATION_SUBMITTED", success: true },
          "*"
        );
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 font-verona" dir="ltr">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-white rounded-xl p-6 md:p-8 shadow-lg border-2 border-neutral-light font-verona"
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6 font-verona"
          >
            <ApplicationPersonalInfo
              countryCode={countryCode}
              setCountryCode={setCountryCode}
            />
            <NationalityFields />
            <GenderField />
            <DateOfBirthFields />
            <AcademicFields
              control={control}
              tierValue={
                tierToEligibilityCriteriaMapping[
                  watch("tier") as keyof typeof tierToEligibilityCriteriaMapping
                ] || ""
              }
              onTierChange={handleTierChange}
            />

            <WorkFields isApplicationForm={true} />
            <LinkedInField name="linkedin_url" />
            <DocumentUpload tier={watch("tier")} />
            <SubmitButton isSubmitting={isSubmitting} />
          </form>
        </FormProvider>
      </motion.div>
    </div>
  );
};

export default ApplicationForm;
